// src/components/PostFeed.jsx
"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  documentId,
  getDocs,
  withLabel, // debug helper to tag queries in logs
} from "@/lib/firestore-instrumented";
import PostCard from "@/components/PostCard";
import CardList from "@/components/CardList";
import CardSkeleton from "@/components/CardSkeleton";
import EmptyStateIllustration from "@/components/EmptyStateIllustration";
import InfiniteScrollSentinel from "@/components/InfiniteScrollSentinel";
import FeedAdSlot from "@/components/ads/FeedAdSlot";
import SingleAdSlot from "@/components/ads/SingleAdSlot";
import { scrollFocusedDocumentTarget } from "@/lib/focusScroll.mjs";
import {
  activeOpportunityByAnchor,
  reconcileFeedAdLedger,
} from "@/lib/ads/feedOpportunities.mjs";
import { hasStableAuthorSnapshot } from "@/lib/contentAuthor.mjs";

/**
 * Props:
 * - scope: "public" | "myHubs" | "hub"
 * - db: Firestore instance
 * - hubSlug?: string
 * - hubType?: "private" | "public-view" | "public-interact"
 * - isMember?: boolean
 * - joinedHubSlugs?: string[]
 * - feedAudienceId?: string
 * - pageSize?: number                 // page size for live and older-page reads
 * - Card?: ({ t }) => JSX.Element
 * - FallbackSkeleton?: JSX.Element
 * - FallbackEmpty?: JSX.Element
 * - pendingPosts?: any[]
 */

const authorCache = new Map(); // uid -> { uid, displayName, username, profilePictureUrl }
const inflightKeys = new Set();
const EMPTY = Object.freeze([]);

function fallbackAuthorForThread(thread) {
  if (!thread?.authorUid) return null;
  const handle = thread.authorHandle || "";
  return {
    uid: thread.authorUid,
    displayName: thread.authorDisplayName || "",
    username: handle,
    username_lowercase: handle,
    profilePictureUrl: thread.authorAvatar || "",
  };
}

function attachCachedAuthors(threads) {
  return threads.map((thread) => {
    if (thread.authorSnapshotReady === true) {
      return {
        ...thread,
        author: thread.author || fallbackAuthorForThread(thread),
        authorPending: false,
      };
    }
    const cachedAuthor = authorCache.get(thread.authorUid) || null;
    return {
      ...thread,
      author: cachedAuthor || fallbackAuthorForThread(thread),
      authorPending: Boolean(
        thread.authorUid && !cachedAuthor && !hasStableAuthorSnapshot(thread)
      ),
    };
  });
}

export default function PostFeed({
  scope = "public",
  db,
  hubSlug,
  hubType,
  isMember = false,
  joinedHubSlugs,
  feedAudienceId = "sproutly",
  pageSize = 20,
  Card = PostCard,
  FallbackSkeleton = <MinimalSkeleton />,
  FallbackEmpty = <MinimalEmpty />,
  pendingPosts = [],
  adPlacement = null,
  shareThreadId = "",
}) {
  // Live page (first page) rows
  const [rows, setRows] = useState(null);
  // Older pages appended here (no listener)
  const [older, setOlder] = useState([]);
  const [error, setError] = useState("");
  const [pending, setPending] = useState([]);
  const [awaitThreadId, setAwaitThreadId] = useState(null);
  const [focusedThreadId, setFocusedThreadId] = useState(null);

  // pagination state
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [adLedger, setAdLedger] = useState([]);
  const [initialFeedReady, setInitialFeedReady] = useState(false);
  const adSessionIdRef = useRef("");
  const paginationStartedRef = useRef(false);
  const loadingMoreRef = useRef(false);

  // Normalize to a stable reference
  const joined = useMemo(
    () => (joinedHubSlugs && joinedHubSlugs.length ? joinedHubSlugs : EMPTY),
    [joinedHubSlugs]
  );
  // Build a spec but keep deps stable to prevent relistening
  const spec = useMemo(() => {
    if (scope === "hub") return { kind: "byHub", hubSlug: hubSlug ?? "" };
    if (scope === "myHubs") return { kind: "byHubs", slugs: joined.slice(0, 50) };
    return { kind: "publicFeed", audienceId: String(feedAudienceId || "").trim() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scope,
    scope === "hub" ? hubSlug : null,
    scope === "myHubs" ? joined : null,
    scope === "public" ? feedAudienceId : null,
  ]);

  const disabled =
    (spec.kind === "byHub" && !spec.hubSlug) ||
    (spec.kind === "byHubs" && (!spec.slugs || spec.slugs.length === 0)) ||
    (spec.kind === "publicFeed" && !spec.audienceId);

  // cache-aware batch fetch
  const fetchUsersForUids = async (uids = []) => {
    const uniq = Array.from(new Set(uids.filter(Boolean)));
    const missing = uniq.filter((uid) => uid && !authorCache.has(uid));
    if (missing.length === 0) return;

    const key = missing.slice().sort().join(",");
    if (inflightKeys.has(key)) return;
    inflightKeys.add(key);

    try {
      const usersCol = collection(db, "users");
      const CHUNK = 10;
      for (let i = 0; i < missing.length; i += CHUNK) {
        const slice = missing.slice(i, i + CHUNK);
        const snap = await getDocs(
          withLabel(query(usersCol, where(documentId(), "in", slice)), `Users:batch size=${slice.length}`)
        );
        snap.forEach((d) => {
          const u = d.data() || {};
          authorCache.set(d.id, {
            uid: d.id,
            displayName: u.displayName || "",
            username: u.username || u.username_lowercase || "",
            profilePictureUrl: u.profilePictureUrl || "",
          });
        });
        slice.forEach((uid) => {
          if (!authorCache.has(uid)) authorCache.set(uid, { uid });
        });
      }
    } finally {
      inflightKeys.delete(key);
    }
  };

  // Effect deps as primitives to prevent accidental resubscribe
  const kind = spec.kind;
  const hubKey = kind === "byHub" ? spec.hubSlug : "";
  const hubsKey = kind === "byHubs" ? spec.slugs.join("|") : "";
  const audienceKey = kind === "publicFeed" ? spec.audienceId : "";

  // Reset pagination whenever spec changes
  useEffect(() => {
    setOlder([]);
    setCursor(null);
    setHasMore(false);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    paginationStartedRef.current = false;
  }, [kind, hubKey, hubsKey, audienceKey, pageSize]);

  useEffect(() => {
    setInitialFeedReady(false);
    setAdLedger([]);
    if (disabled) {
      setRows((prev) => (prev === null ? [] : prev));
      setError("");
      setHasMore(false);
      setCursor(null);
      setInitialFeedReady(true);
      return;
    }

    console.debug(
      `[PostFeed] mount scope=${scope} kind=${kind}` +
        (kind === "byHub" ? ` hub=${hubKey}` : "") +
        (kind === "byHubs" ? ` hubs=[${hubsKey}]` : "") +
        (kind === "publicFeed" ? ` audience=${audienceKey}` : "") +
        ` pageSize=${pageSize}`
    );

    const unsubs = [];
    const trackUnsub = (u, label) => {
      unsubs.push(() => {
        try {
          u();
          console.debug(`[PostFeed] unsub ${label}`);
        } catch {}
      });
    };

    const startByHub = () => {
      const base = collection(db, `discussions/${hubKey}/threads`);
      const qRef = withLabel(
        query(base, orderBy("lastActivityAt", "desc"), limit(pageSize)),
        `Home:byHub:${hubKey}:threads(limit)`
      );
      console.debug(`[PostFeed] listen start label=${qRef.__label}`);
      const unsub = onSnapshot(
        qRef,
        async (snap) => {
          console.debug(`[PostFeed] snapshot label=${qRef.__label} size=${snap.size}`);
          const docs = snap.docs;
          const next = docs.map((d) => {
            const data = d.data() || {};
            const derivedHubSlug = d?.ref?.parent?.parent?.id || "";
            return { id: d.id, ...data, hubSlug: data.hubSlug || derivedHubSlug };
          });
          setRows(attachCachedAuthors(next));
          setError("");
          setInitialFeedReady(true);

          const uids = next
            .filter((t) => t.authorSnapshotReady !== true)
            .map((t) => t.authorUid)
            .filter(Boolean);
          fetchUsersForUids(uids)
            .then(() => {
              setRows((current) => {
                if (!Array.isArray(current)) return current;
                return attachCachedAuthors(current);
              });
            })
            .catch((e) => {
              console.warn("[PostFeed] author enrichment failed:", e);
            });

          setCursor(docs.length > 0 ? docs[docs.length - 1] : null);
          setHasMore(docs.length === pageSize);
        },
        (e) => {
          console.error("PostFeed onSnapshot error (byHub):", e);
          setRows([]);
          setError("Could not load posts.");
          setHasMore(false);
          setCursor(null);
          setInitialFeedReady(true);
        }
      );
      trackUnsub(unsub, qRef.__label);
    };

    const startByHubs = () => {
      const perHub = new Map(); // slug -> array of rows
      const failedHubs = new Set();
      const slugs = hubsKey ? hubsKey.split("|") : [];
      const settledHubs = new Set();

      const recompute = async () => {
        const merged = Array.from(perHub.values()).flat();
        const withAuthors = attachCachedAuthors(merged);
        withAuthors.sort((a, b) => (b.lastActivityAt?.toMillis?.() || 0) - (a.lastActivityAt?.toMillis?.() || 0));
        setRows(withAuthors.slice(0, pageSize));
        setError(failedHubs.size === slugs.length ? "Could not load posts." : "");
        const uids = merged
          .filter((t) => t.authorSnapshotReady !== true)
          .map((t) => t.authorUid)
          .filter(Boolean);
        fetchUsersForUids(uids)
          .then(() => {
            setRows((current) => {
              if (!Array.isArray(current)) return current;
              return attachCachedAuthors(current).sort(
                (a, b) => (b.lastActivityAt?.toMillis?.() || 0) - (a.lastActivityAt?.toMillis?.() || 0)
              );
            });
          })
          .catch((e) => {
            console.warn("[PostFeed] author enrichment failed:", e);
          });
        // For simplicity, we skip paging across hubs for now
        setCursor(null);
        setHasMore(false);
      };

      slugs.forEach((slug) => {
        const base = collection(db, `discussions/${slug}/threads`);
        const qRef = withLabel(
          query(base, orderBy("lastActivityAt", "desc"), limit(pageSize)),
          scope === "public"
            ? `Home:public:${slug}:threads(limit)`
            : `Home:myHubs:${slug}:threads(limit)`
        );
        console.debug(`[PostFeed] listen start label=${qRef.__label}`);
        const unsub = onSnapshot(
          qRef,
          async (snap) => {
            console.debug(`[PostFeed] snapshot label=${qRef.__label} size=${snap.size}`);
            failedHubs.delete(slug);
            perHub.set(
              slug,
              snap.docs.map((d) => {
                const data = d.data() || {};
                return { id: d.id, ...data, hubSlug: data.hubSlug || slug };
              })
            );
            settledHubs.add(slug);
            await recompute();
            if (settledHubs.size === slugs.length) setInitialFeedReady(true);
          },
          (e) => {
            console.error(`PostFeed onSnapshot error (${scope}:${slug}):`, e);
            failedHubs.add(slug);
            perHub.set(slug, []);
            settledHubs.add(slug);
            recompute();
            if (settledHubs.size === slugs.length) setInitialFeedReady(true);
          }
        );
        trackUnsub(unsub, qRef.__label);
      });
    };

    const startPublicFeed = () => {
      const base = collection(db, "publicFeedThreads");
      const qRef = withLabel(
        query(
          base,
          where("audienceIds", "array-contains", audienceKey),
          where("publicReadable", "==", true),
          orderBy("lastActivityAt", "desc"),
          orderBy(documentId(), "desc"),
          limit(pageSize)
        ),
        `Home:publicFeed:${audienceKey}:threads(limit=${pageSize})`
      );
      console.debug(`[PostFeed] listen start label=${qRef.__label}`);
      const unsub = onSnapshot(
        qRef,
        (snap) => {
          console.debug(`[PostFeed] snapshot label=${qRef.__label} size=${snap.size}`);
          const next = snap.docs.map((d) => {
            const data = d.data() || {};
            return {
              ...data,
              id: data.threadId || d.id,
              feedEntryId: d.id,
            };
          });
          setRows(attachCachedAuthors(next));
          setError("");
          setInitialFeedReady(true);
          if (!paginationStartedRef.current) {
            setCursor(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
            setHasMore(snap.docs.length === pageSize);
          }
        },
        (e) => {
          console.error("PostFeed onSnapshot error (publicFeed):", e);
          setRows([]);
          setError("Could not load posts.");
          setHasMore(false);
          setCursor(null);
          setInitialFeedReady(true);
        }
      );
      trackUnsub(unsub, qRef.__label);
    };

    if (kind === "byHub") startByHub();
    else if (kind === "byHubs") startByHubs();
    else startPublicFeed();

    return () => {
      unsubs.forEach((u) => u());
    };
    // deps are stable primitives, not objects/arrays
  }, [db, kind, hubKey, hubsKey, audienceKey, pageSize, disabled, scope]);

  // Older pages use bounded one-time reads; the end sentinel calls this once
  // per viewport entry and the ref closes the state-update race window.
  const handleLoadMore = async () => {
    if (!cursor || loadingMoreRef.current || !["byHub", "publicFeed"].includes(kind)) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      let base, qRef;
      if (kind === "publicFeed") {
        base = collection(db, "publicFeedThreads");
        qRef = withLabel(
          query(
            base,
            where("audienceIds", "array-contains", audienceKey),
            where("publicReadable", "==", true),
            orderBy("lastActivityAt", "desc"),
            orderBy(documentId(), "desc"),
            startAfter(cursor),
            limit(pageSize)
          ),
          `Home:publicFeed:${audienceKey}:threads:loadMore`
        );
      } else {
        base = collection(db, `discussions/${hubKey}/threads`);
        qRef = withLabel(
          query(base, orderBy("lastActivityAt", "desc"), startAfter(cursor), limit(pageSize)),
          `Home:byHub:${hubKey}:threads:loadMore`
        );
      }

      const snap = await getDocs(qRef);
      const docs = snap.docs;
      const next = docs.map((d) => {
        const data = d.data() || {};
        if (kind === "publicFeed") {
          return {
            ...data,
            id: data.threadId || d.id,
            feedEntryId: d.id,
          };
        }
        const derivedHubSlug = d?.ref?.parent?.parent?.id || "";
        return { id: d.id, ...data, hubSlug: data.hubSlug || derivedHubSlug };
      });

      paginationStartedRef.current = true;
      setOlder((curr) => [...curr, ...attachCachedAuthors(next)]);
      const uids = next
        .filter((t) => t.authorSnapshotReady !== true)
        .map((t) => t.authorUid)
        .filter(Boolean);
      fetchUsersForUids(uids)
        .then(() => {
          setOlder((current) => attachCachedAuthors(current));
        })
        .catch((e) => {
          console.warn("[PostFeed] author enrichment failed:", e);
        });
      setCursor(docs.length > 0 ? docs[docs.length - 1] : cursor);
      setHasMore(docs.length === pageSize);
    } catch (e) {
      console.error("[PostFeed] loadMore error:", e);
      // keep hasMore/cursor as-is on error
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  // Optimistic events: pending shimmer, resolve, await-then-scroll
  useEffect(() => {
    const onPending = (e) => {
      const p = e.detail || {};
      if (!p?.hubSlug || !p?.tempId) return;
      // Home eligibility is server-owned. Do not flash an optimistic post before
      // the materialized index has confirmed that it belongs in this audience.
      if (spec.kind === "publicFeed") return;
      if (spec.kind === "byHub" && p.hubSlug !== spec.hubSlug) return;
      if (spec.kind === "byHubs" && !spec.slugs?.includes(p.hubSlug)) return;
      setPending((curr) => [{ id: p.tempId, hubSlug: p.hubSlug }, ...curr]);
    };
    const onResolve = (e) => {
      const { tempId } = e.detail || {};
      if (!tempId) return;
      setPending((curr) => curr.filter((x) => x.id !== tempId));
    };
    const onAwait = (e) => {
      const { threadId } = e.detail || {};
      if (!threadId) return;
      setAwaitThreadId(threadId);
      setFocusedThreadId(threadId);
    };

    window.addEventListener("post:pending", onPending);
    window.addEventListener("post:resolve", onResolve);
    window.addEventListener("post:await", onAwait);
    return () => {
      window.removeEventListener("post:pending", onPending);
      window.removeEventListener("post:resolve", onResolve);
      window.removeEventListener("post:await", onAwait);
    };
  }, [spec]);

  // When rows update, if the awaited thread is present, scroll to it once.
  useEffect(() => {
    if (!awaitThreadId) return;
    const all = Array.isArray(rows) ? rows.concat(older) : older;
    if (!Array.isArray(all) || all.length === 0) return;
    const found = all.some((t) => t?.id === awaitThreadId);
    if (!found) return;
    const el = document.getElementById(`thread-${awaitThreadId}`);
    if (el) {
      scrollFocusedDocumentTarget(el);
      setAwaitThreadId(null);
    }
  }, [rows, older, awaitThreadId]);

  const allRows = useMemo(() => {
    const unique = new Map();
    for (const row of Array.isArray(rows) ? rows : []) unique.set(row.id, row);
    for (const row of older) {
      if (!unique.has(row.id)) unique.set(row.id, row);
    }
    return [...unique.values()];
  }, [rows, older]);
  const rowIdsKey = allRows.map((row) => row.id).join("|");
  const adPlacementKey = adPlacement
    ? `${adPlacement.enabled}:${adPlacement.placementId}:${adPlacement.firstAfter}:${adPlacement.interval}:${adPlacement.maximum}`
    : "disabled";

  useEffect(() => {
    if (!adSessionIdRef.current) {
      adSessionIdRef.current = `feed-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
    setAdLedger((previous) => reconcileFeedAdLedger({
      rows: initialFeedReady ? allRows : [],
      previous,
      placement: adPlacement,
      sessionId: adSessionIdRef.current,
    }));
    // The primitive keys intentionally isolate the effect from Firestore row object churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIdsKey, adPlacementKey, initialFeedReady]);

  const adByAnchor = useMemo(() => activeOpportunityByAnchor(adLedger), [adLedger]);

  if (rows === null) return FallbackSkeleton;
  if (error)
    return (
      <div className="edge-mobile">
        <div className="card square-mobile text-sm text-red-600">{error}</div>
      </div>
    );
  if (rows.length === 0 && pendingPosts.length === 0 && pending.length === 0) return FallbackEmpty;

  return (
    <>
      <CardList>
        {[...pendingPosts, ...pending].map((p, i) => (
          <MinimalSkeleton key={`pending-${p.id ?? i}`} />
        ))}

        {allRows.map((t) => {
          const opportunity = adByAnchor.get(t.id);
          return (
            <Fragment key={t.id}>
              <Card
                t={t}
                hubSlug={(kind === "byHub" ? hubKey : t.hubSlug) || t.hubSlug || ""}
                focused={focusedThreadId === t.id}
              />
              {shareThreadId === t.id ? (
                <SingleAdSlot placementId="share.thread.after" />
              ) : null}
              {opportunity ? (
                <FeedAdSlot key={opportunity.opportunityId} opportunity={opportunity} />
              ) : null}
            </Fragment>
          );
        })}
      </CardList>

      <InfiniteScrollSentinel
        enabled={["byHub", "publicFeed"].includes(kind) && hasMore}
        loading={loadingMore}
        onLoadMore={handleLoadMore}
      />
    </>
  );
}

function MinimalSkeleton() {
  return <CardSkeleton />;
}

function MinimalEmpty() {
  return (
    <div className="edge-mobile">
      <div className="card square-mobile text-center">
        <EmptyStateIllustration />
        <h4 className="font-semibold">It’s quiet…</h4>
        <p className="mt-1 text-sm text-[var(--muted)]">Be the first to start a conversation.</p>
      </div>
    </div>
  );
}
